import { createSlice } from '@reduxjs/toolkit';
import concat from 'lodash-es/concat';
import random from 'lodash/random';

import { AtiraLinkAxios } from '../../axios/atira-link/atira-link.axios';
import { AtiraLink } from '../../model/atira-link/AtiraLink';
import { AtiraLinkStatsResponseDto } from '../../model/atira-link/dto/AtiraLinkStatsResponseDto';
import { CreateAtiraLinkDto } from '../../model/atira-link/dto/CreateAtiraLinkDto';
import { EditAtiraLinkDto } from '../../model/atira-link/dto/EditAtiraLinkDto';
import { GetUserAtiraLinkResponseDto } from '../../model/atira-link/dto/GetUserAtiraLinksResponseDto';
import { AtiraLinkSocialHandlers } from '../../model/atira-link/types/AtiraLinkSocialMediaHandlers.enum';
import { PageDto } from '../../model/meta/PageDto';
import { AtiraLinkSettings } from '../../model/settings/types/AtiraLinkSettings';
import { GenericWithUserId } from '../../model/shared/GenericWithUserId';
import { AtiraLinkTheme } from '../../model/theme/AtiraLinkTheme';
import { GetThemesResponseDto } from '../../model/theme/dto/GetThemesResponseDto';
import { AtiraThunk } from '../AtiraThunk';
import { userActions } from '../user/user.slice';
import { DeleteAtiraLinkDto } from './dto/DeleteAtiraLinkDto';
import { GenericDisableEnableAtiraLinkDto } from './dto/GenericDisableEnableAtiraLinkDto';
import { GetThemesDto } from './dto/GetThemesDto';

interface AtiraLinkReducer {
  atiraLinks?: AtiraLink[];
  atiraLinkLoading?: boolean;
  selectedAtiraLinkStats?: AtiraLinkStatsResponseDto;
  settings?: AtiraLinkSettings[];
  socialsClickCount?: Record<AtiraLinkSocialHandlers, number>;
  //
  themes: AtiraLinkTheme[];
  themesDataByName: Record<string, Partial<AtiraLink> & { fake?: boolean }>;
  themesLoading?: boolean;
  themesPage?: number;
  themesPageHasNext?: boolean;
  selectedAtiraLink?: AtiraLink;
}

const initialState = Object.freeze<AtiraLinkReducer>({
  atiraLinks: [],
  atiraLinkLoading: false,
  selectedAtiraLinkStats: undefined,
  socialsClickCount: undefined,
  settings: undefined,
  themes: [],
  themesPage: 0,
  themesPageHasNext: true,
  themesDataByName: {},
  themesLoading: false,
  selectedAtiraLink: undefined,
});

const createAtiraLink = AtiraThunk<AtiraLink, CreateAtiraLinkDto>(
  `/atira-link/create`,
  AtiraLinkAxios.createAtiraLink,
);

const editAtiraLink = AtiraThunk<void, EditAtiraLinkDto>(
  `/atira-link/edit`,
  AtiraLinkAxios.editAtiraLinkDto,
);

const getUserAtiraLinks = AtiraThunk<
  GetUserAtiraLinkResponseDto,
  GenericWithUserId
>(`/atira-link/get`, (dto) => AtiraLinkAxios.getUserAtiraLinks(dto));

const deleteUserAtiraLink = AtiraThunk<void, DeleteAtiraLinkDto>(
  `/atira-link/delete`,
  (dto) => AtiraLinkAxios.deleteAtiraLink(dto),
);

const disableUserAtiraLink = AtiraThunk<void, GenericDisableEnableAtiraLinkDto>(
  `/atira-link/disable`,
  (dto) => AtiraLinkAxios.disableAtiraLink(dto),
);

const enableUserAtiraLink = AtiraThunk<void, GenericDisableEnableAtiraLinkDto>(
  `/atira-link/enable`,
  (dto) => AtiraLinkAxios.enableAtiraLink(dto),
);

const getThemes = AtiraThunk<GetThemesResponseDto, GetThemesDto>(
  `/atira-link/themes`,
  AtiraLinkAxios.getThemes,
);

const AtiraLinkSlice = createSlice({
  name: 'atiraLinkSlice',
  initialState,
  reducers: {
    setSelectedAtiraLink(state, action) {
      state.selectedAtiraLink = action.payload;
    },

    resetSelectedAtiraLink(state) {
      state.selectedAtiraLink = undefined;
    },

    resetTemplateMeta(state) {
      state.themesPage = 0;
      state.themesPageHasNext = true;
      state.themes = [];
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getUserAtiraLinks.pending, (state) => {
      state.atiraLinkLoading = true;
    });
    addCase(getUserAtiraLinks.fulfilled, (state, action) => {
      state.atiraLinks = action.payload.links;

      state.selectedAtiraLink =
        action.payload.links.find(
          (link) => link._id === state.selectedAtiraLink?._id,
        ) || action.payload.links[0];

      const selectedLinkMappedStats = action.payload.mappedStats?.find(
        (stats) => stats.linkId === state.selectedAtiraLink?._id,
      );

      state.selectedAtiraLinkStats = selectedLinkMappedStats;

      state.socialsClickCount = selectedLinkMappedStats?.SOCIAL?.reduce(
        (acc, social) => {
          const matchedStat = selectedLinkMappedStats?.SOCIAL?.find(
            (stat) => stat.handler === social.handler,
          );

          acc[social.handler as AtiraLinkSocialHandlers] = matchedStat
            ? matchedStat.count
            : 0;

          return acc;
        },
        {} as Record<AtiraLinkSocialHandlers, number>,
      );

      state.atiraLinkLoading = false;
    });
    addCase(getUserAtiraLinks.rejected, (state) => {
      state.atiraLinkLoading = false;
    });

    addCase(getThemes.pending, (state) => {
      state.themesLoading = true;
    });
    addCase(getThemes.fulfilled, (state, action) => {
      const meta = action.payload.meta as PageDto;
      if (meta.page === 1) {
        state.themes = action.payload.data;
      } else {
        state.themes = concat(state.themes, action.payload.data);
      }

      action.payload.data.forEach((theme) => {
        if (state.themesDataByName) {
          state.themesDataByName[theme.name] = {
            linkName: '',
            customLinks: [{ label: 'Custom Link', value: '#' }],
            title: 'My Atira Link',
            bio: 'This is a short bio about my Atira link.',
            email: { label: 'Email', value: '' },
            phone: { label: 'Phone', value: '90xxx-xxx-xxx' },
            website: { label: 'My site', value: 'www.example.com' },
            map: {
              label: 'My location',
              value: 'https://maps.google.com?q=My+Location',
            },
            socials: [
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.INSTAGRAM,
                url: 'https://instagram.com',
                label: AtiraLinkSocialHandlers.INSTAGRAM,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.TWITTER,
                url: 'https://twitter.com',
                label: AtiraLinkSocialHandlers.TWITTER,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.YOUTUBE,
                url: 'https://youtube.com',
                label: AtiraLinkSocialHandlers.YOUTUBE,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.FACEBOOK,
                url: 'https://facebook.com',
                label: AtiraLinkSocialHandlers.FACEBOOK,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.LINKEDIN,
                url: 'https://linkedin.com',
                label: AtiraLinkSocialHandlers.LINKEDIN,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.WHATSAPP,
                url: 'https://whatsapp.com',
                label: AtiraLinkSocialHandlers.WHATSAPP,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.TIKTOK,
                url: 'https://tiktok.com',
                label: AtiraLinkSocialHandlers.TIKTOK,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.SNAPCHAT,
                url: 'https://snapchat.com',
                label: AtiraLinkSocialHandlers.SNAPCHAT,
              },
              {
                parentId: '',
                handle: AtiraLinkSocialHandlers.TELEGRAM,
                url: 'https://telegram.com',
                label: AtiraLinkSocialHandlers.TELEGRAM,
              },
            ]
              .map((s, index) => ({ ...s, order: index }))
              .slice(0, random(1, 8)),
            image: 'https://picsum.photos/200/300',
            fake: true,
          };
        }
      });
      state.themesLoading = false;
      state.themesPage = meta.page;
      state.themesPageHasNext = meta.hasNext;
    });
    addCase(getThemes.rejected, (state) => {
      state.themesLoading = false;
    });

    //@ts-ignore
    addCase(userActions.logout.fulfilled, (state: AtiraLinkReducer) => {
      return (state = initialState);
    });
  },
});

export const AtiraLinkActions = {
  ...AtiraLinkSlice.actions,
  createAtiraLink,
  editAtiraLink,
  getUserAtiraLinks,
  deleteUserAtiraLink,
  disableUserAtiraLink,
  enableUserAtiraLink,
  getThemes,
};

export default AtiraLinkSlice.reducer;
